import type { ReactNode, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { useRef } from 'react';

import { cn } from '@april9au/react-common/utils';

import type { Point, Zone } from '@/chat/activity';
import type { ImageField } from '@prismicio/client';
import { PrismicMedia } from '@/components/ui/media';
import { answerKey } from '@/lib/autopilot';

/**
 * The picture an activity is played on.
 *
 * Two jobs, both about the thread not moving: the stage reserves the image's
 * own aspect ratio before the file loads, and the reveal overlay is a second
 * absolutely-positioned image over the first, so showing the answer never
 * changes the card's height.
 *
 * Percentage geometry is converted here and nowhere else — `pointAt` is the
 * only place the DOM turns into the coordinates `app/chat/activity.ts` marks.
 */

/** The aspect ratio Prismic reports, with the activity photos' 4:3 as the fallback. */
function ratioOf(image: ImageField): string {
  return image.dimensions ? `${image.dimensions.width} / ${image.dimensions.height}` : '4 / 3';
}

/** Where a pointer landed, in percent of the stage, clamped to the picture. */
export function pointAt(stage: HTMLElement, event: { clientX: number; clientY: number }): Point {
  const box = stage.getBoundingClientRect();
  const clamp = (value: number) => Math.min(100, Math.max(0, value));

  return {
    x: clamp(((event.clientX - box.left) / box.width) * 100),
    y: clamp(((event.clientY - box.top) / box.height) * 100),
  };
}

/** The inline style that places a percentage box over the stage. */
export const zoneStyle = (zone: Zone) => ({
  left: `${zone.x}%`,
  top: `${zone.y}%`,
  width: `${zone.w}%`,
  height: `${zone.h}%`,
});

/** How far past the answer's own box a search area reaches, in percent of the picture. */
const HINT_PADDING = 10;

/**
 * One box around every correct zone, padded by `HINT_PADDING` and clamped to
 * the picture. Looser than the marking on purpose, so the hint still leaves
 * something to find.
 */
export function hintArea(zones: Array<Zone>): Zone | null {
  const correct = zones.filter((zone) => zone.correct);

  if (correct.length === 0) {
    return null;
  }

  const left = Math.max(0, Math.min(...correct.map((zone) => zone.x)) - HINT_PADDING);
  const top = Math.max(0, Math.min(...correct.map((zone) => zone.y)) - HINT_PADDING);
  const right = Math.min(100, Math.max(...correct.map((zone) => zone.x + zone.w)) + HINT_PADDING);
  const bottom = Math.min(100, Math.max(...correct.map((zone) => zone.y + zone.h)) + HINT_PADDING);

  return { label: '', x: left, y: top, w: right - left, h: bottom - top, correct: true };
}

export type StageProps = {
  image: ImageField;
  revealImage?: ImageField;
  revealed: boolean;
  /** Armed stages take pointers; frozen ones are a picture of what happened. */
  interactive: boolean;
  onPointerDown?: (event: ReactPointerEvent<HTMLDivElement>) => void;
  stageRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  children?: ReactNode;
};

export function Stage({
  image,
  revealImage,
  revealed,
  interactive,
  onPointerDown,
  stageRef,
  className,
  children,
}: StageProps) {
  const fallbackRef = useRef<HTMLDivElement>(null);
  const showReveal = revealed && revealImage?.url !== undefined && revealImage.url !== null;

  return (
    <div
      ref={stageRef ?? fallbackRef}
      onPointerDown={onPointerDown}
      style={{ aspectRatio: ratioOf(image) }}
      className={cn(
        'bg-chat-rail relative w-full touch-none overflow-hidden rounded-xl select-none',
        // A 1 px inset outline keeps the photo's edge from bleeding into the card.
        'outline -outline-offset-1 outline-black/10',
        interactive && 'cursor-crosshair',
        className,
      )}
    >
      {image.url === null ? null : (
        <PrismicMedia
          image={image}
          draggable={false}
          priority
          placeholder="var(--chat-rail)"
          className="absolute inset-0 size-full object-cover"
        />
      )}

      {showReveal ? (
        <PrismicMedia
          image={revealImage}
          draggable={false}
          priority
          placeholder="transparent"
          className="activity-reveal-in absolute inset-0 size-full object-contain"
        />
      ) : null}

      {children}
    </div>
  );
}

/** The search area over the picture. Taps pass through it to the stage. */
export function HintArea({ zone, label }: { zone: Zone; label: string }) {
  return (
    <span
      aria-hidden
      style={zoneStyle(zone)}
      className="activity-hint-in border-surf-yellow pointer-events-none absolute rounded-xl border-2 border-dashed shadow-[0_0_0_9999px_rgb(0_0_0/0.28)]"
    >
      <span className="bg-surf-yellow text-surf-dark-blue absolute start-2 -top-3 rounded-full px-2 py-0.5 text-[11px] font-bold tracking-[0.08em] uppercase">
        {label}
      </span>
    </span>
  );
}

/**
 * A focusable zone over the picture: the keyboard's way in.
 *
 * `pointer-events-none` keeps a tap landing where the finger actually is —
 * these buttons exist for focus and Enter, not for pointers, and a keyboard
 * activation still fires their click.
 *
 * The Autopilot uses them as its way in too, and `answerKey` tells it which
 * region is the right one — compiled out of a production build.
 */
export function ZoneButton({
  zone,
  label,
  onSelect,
  disabled,
  highlighted,
}: {
  zone: Zone;
  label: string;
  onSelect: () => void;
  disabled: boolean;
  highlighted: boolean;
}) {
  return (
    <button
      type="button"
      data-autopilot="zone"
      {...answerKey(zone.correct)}
      disabled={disabled}
      onClick={onSelect}
      style={zoneStyle(zone)}
      aria-label={label}
      className={cn(
        'absolute cursor-pointer rounded-lg border-2 border-dashed border-transparent',
        'pointer-events-none focus-visible:border-white focus-visible:outline-none',
        'focus-visible:ring-surf-blue ring-inset focus-visible:ring-[3px]',
        highlighted && 'border-answer-correct bg-answer-correct/15 border-solid',
      )}
    />
  );
}
